import { AttachMoney, DeleteOutline, Edit, ExpandMore, LocationOn } from '@mui/icons-material';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Nullable } from 'global';
import { useEquipmentDelivery } from 'modules/equipment';
import type { QuoteEquipmentResponse } from '@trnsact/trnsact-shared-types';
import { DealBuilderEquipmentSource } from '../../../../types';
import { FlatAccordion } from '../dealTab/FlatAccordion';
import { PriceField } from '../dealTab/PriceField';

interface Props {
  item: DealBuilderEquipmentSource;
  quoteEquipment: Nullable<QuoteEquipmentResponse>;
  onEdit: (equipmentId: string) => void;
  onDelete: (equipmentId: string) => void;
}

const formatCondition = (newUsed: Nullable<string>): string =>
  newUsed ? newUsed.charAt(0).toUpperCase() + newUsed.slice(1) : '-';

const DetailField = ({ label, value }: { label: string; value: Nullable<string> }) => (
  <Box>
    <Typography variant="body2" color="text.secondary">
      {label}:
    </Typography>
    <Typography fontWeight={500}>{value || '-'}</Typography>
  </Box>
);

export const EquipmentDetailCard = ({ item, quoteEquipment, onEdit, onDelete }: Props) => {
  const { deliveryAddress } = useEquipmentDelivery(item.equipmentId);
  const title = [item.year, item.make, item.model].filter(Boolean).join(' ');
  const addressLine = [deliveryAddress?.streetAddress].filter(Boolean).join(' ');
  const stateZip = [deliveryAddress?.state, deliveryAddress?.postalCode].filter(Boolean).join(' ');
  const addressLocality = [deliveryAddress?.city, stateZip].filter(Boolean).join(', ');
  const customerPrice = quoteEquipment?.price?.netPrice ?? item.retailPrice;
  const wholesale = quoteEquipment?.price?.wholesalePrice ?? item.wholesaleCost;
  const retail = quoteEquipment?.price?.retailPrice ?? item.retailPrice;

  return (
    <Box p={2} borderRadius={1} border={1} borderColor="grey.200" bgcolor="#f7f8fa">
      <Stack gap={1}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Stack direction="row" gap={0.5}>
            <Tooltip title="Edit Equipment" placement="top">
              <IconButton
                size="small"
                color="primary"
                className="icon-btn-bordered"
                onClick={() => {
                  onEdit(item.equipmentId);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Equipment" placement="top">
              <IconButton
                size="small"
                color="error"
                className="icon-btn-bordered"
                onClick={() => {
                  onDelete(item.equipmentId);
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" flexWrap="wrap" sx={{ columnGap: 6, rowGap: 2 }}>
          <DetailField label="Condition" value={formatCondition(item.newUsed)} />
          <DetailField label="Year" value={item.year} />
          <DetailField label="Make" value={item.make} />
          <DetailField label="Model" value={item.model} />
          <DetailField label="VIN" value={item.serialNumberVin} />
          <DetailField label="Mileage" value={item.mileage != null ? item.mileage.toLocaleString() : null} />
        </Stack>

        <Divider />

        <FlatAccordion disableGutters sx={{ bgcolor: '#f7f8fa' }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography fontWeight={500}>Additional Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              <Box>
                <Stack direction="row" alignItems="center" gap={0.5} mb={1}>
                  <AttachMoney color="primary" fontSize="small" />
                  <Typography variant="body1" fontWeight={400}>
                    Pricing & Margin
                  </Typography>
                </Stack>
                <Stack direction="row" flexWrap="wrap" sx={{ columnGap: 6, rowGap: 2 }}>
                  <PriceField label="Customer Price" value={customerPrice} />
                  <PriceField label="Wholesale" value={wholesale} />
                  <PriceField label="Retail" value={retail} />
                </Stack>
              </Box>

              {addressLine || addressLocality ? (
                <Stack gap={0.5}>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <LocationOn color="primary" fontSize="small" />
                    <Typography variant="body1" fontWeight={400}>
                      Delivery Address
                    </Typography>
                  </Stack>
                  <Stack direction="row" gap={0.5}>
                    {addressLine && (
                      <Typography variant="body1" color="text.primary">
                        {addressLine}
                      </Typography>
                    )}
                    {addressLocality && (
                      <Typography variant="body1" color="text.secondary">
                        {addressLocality}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          </AccordionDetails>
        </FlatAccordion>

        <Divider />
      </Stack>
    </Box>
  );
};
